import { isEmpty, isEqual, isNil, isString, omit, union } from 'lodash'

// Keys that say nothing about what a prompt asks the model: server-managed metadata, the client-only link to a
// criterion, and the test cases, which are written through their own endpoints.
const nonContentPromptKeys = [
  'id',
  'scid',
  'prompt_uuid',
  'prompt_tests',
  'created_at',
  'updated_at',
  'created_by',
  'updated_by',
]

// The criteria form hands back '' for a field the API returned as null, and strings for its numeric fields, so
// neither difference means the prompt changed.
const normalizePromptValue = (value) => {
  if (isNil(value) || value === '') {
    return null
  }

  if (isString(value) && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    return Number(value)
  }

  return value
}

// `llm_model_id` supersedes the legacy `model` name, and the criteria form sends the id while clearing the name.
// Dropping the name where an id decides the model keeps that rewrite from reading as a prompt change.
const normalizePrompt = (prompt) => ({
  ...prompt,
  model: isNil(prompt.llm_model_id) ? prompt.model : null,
})

/**
 * Does this prompt differ from the version the API gave us?
 *
 * Saving a scorecard mints a new version of every prompt it sends, so the save path sends only the prompts whose
 * content actually changed - otherwise renaming a scorecard would version every criterion's prompt.
 *
 * @param {object} prompt - a prompt as it would be sent to the API
 * @param {object[]} pristinePrompts - the prompts as the API last returned them
 */
export const promptHasChangesHelper = (prompt, pristinePrompts = []) => {
  const pristinePrompt = pristinePrompts.find(
    (candidate) =>
      (prompt.id && candidate.id === prompt.id) || (prompt.scid && candidate.scid === prompt.scid)
  )

  // A prompt the API has never seen always needs saving.
  if (isNil(pristinePrompt)) {
    return true
  }

  const saving = normalizePrompt(prompt)
  const saved = normalizePrompt(pristinePrompt)
  const contentKeys = union(Object.keys(saving), Object.keys(saved)).filter(
    (key) => !nonContentPromptKeys.includes(key)
  )

  return contentKeys.some(
    (key) => !isEqual(normalizePromptValue(saving[key]), normalizePromptValue(saved[key]))
  )
}

/**
 * The prompts out of `promptIds` whose API version we already hold.
 *
 * The prompts table is insert-only — a given id's text never changes — so a prompt still matching its baseline has
 * nothing to learn from another GET, and a save that just created one hands back everything a GET would. One that
 * was edited locally is not reusable: re-fetching is how the editor drops an unsaved draft.
 *
 * @param {number[]} promptIds - the prompt ids about to be fetched
 * @param {object[]} prompts - the prompts currently in the store
 * @param {object[]} pristinePrompts - the prompts as the API last returned them
 */
export const reusablePromptsHelper = (promptIds, prompts, pristinePrompts) => {
  return prompts.filter(
    (prompt) => promptIds.includes(prompt.id) && !promptHasChangesHelper(prompt, pristinePrompts)
  )
}

/**
 * Fill in each prompt's `llm_model_id` from its legacy `model` name.
 *
 * Prompts written before model ids carry only the name, which leaves every consumer to re-derive the id — and the
 * criteria form reaches for the org default before the name, so opening one of those criteria proposed a different
 * model than the one it actually runs on, and saving moved it there. Resolving on the way into the store means the
 * rest of the app reads one field.
 *
 * Only ever fills a blank, so it is safe to run again whenever the model options land.
 *
 * @param {object[]} prompts - the prompts entering the store
 * @param {object} modelOptions - the loaded model options, keyed by feature
 */
export const resolvePromptModelIdsHelper = (prompts, modelOptions) => {
  const options = modelOptions.qa_copilot

  if (isEmpty(options)) {
    return prompts
  }

  return prompts.map((prompt) => {
    if (!isNil(prompt.llm_model_id) || isNil(prompt.model)) {
      return prompt
    }

    const namedOption = options.find((option) => option.name === prompt.model)

    return isNil(namedOption) ? prompt : { ...prompt, llm_model_id: namedOption.value }
  })
}

export const updatePromptHelper = (prompts, updatedPrompt) => {
  const newPrompts = prompts.filter(
    (prompt) =>
      (prompt.id && prompt.id !== updatedPrompt.id) ||
      (prompt.scid && prompt.scid !== updatedPrompt.scid)
  )
  newPrompts.push(updatedPrompt)
  return newPrompts
}

export const removePromptHelper = (prompts, scid) => {
  return prompts.filter((prompt) => prompt.scid !== scid)
}

export const addTemporaryPromptHelper = (scid) => {
  return {
    user_prompt: '',
    side: 'both',
    feature: 'qa_copilot',
    scid,
  }
}

// `llm_model_id` is the model signal the prompt API runs on; the legacy `model`
// name is only its fallback for rows written before ids existed. Sending both
// lets them diverge — a fresh id sitting next to the name of the model it
// replaced — so drop the name whenever we have an id.
//
// A name is kept only where it is the one usable signal left: no id, and the name
// actually names something. An empty name is no fallback at all, so it goes too
// rather than travelling as one.
//
// Apply this to every body sent to a prompt endpoint, including the ones built by
// spreading a fetched prompt record back out.
export const withoutLegacyModel = (prompt = {}) =>
  prompt.llm_model_id || isEmpty(prompt.model) ? omit(prompt, ['model']) : prompt

export const addPromptTestHelper = (prompts, promptToTest, promptTest) => {
  const newPrompts = prompts.filter(
    (prompt) =>
      (prompt.id && prompt.id !== promptToTest.id) ||
      (prompt.scid && prompt.scid !== promptToTest.scid)
  )
  const newPrompt = {
    ...promptToTest,
    prompt_tests: [...promptToTest.prompt_tests, promptTest],
  }
  newPrompts.push(newPrompt)
  return newPrompts
}

export const deletePromptTestHelper = (prompts, promptToTest, promptTestToDelete) => {
  const newPrompts = prompts.filter(
    (prompt) =>
      (prompt.id && prompt.id !== promptToTest.id) ||
      (prompt.scid && prompt.scid !== promptToTest.scid)
  )
  const newPrompt = {
    ...promptToTest,
    prompt_tests: promptToTest.prompt_tests.filter(
      (promptTest) => promptTest.id !== promptTestToDelete.id
    ),
  }
  newPrompts.push(newPrompt)
  return newPrompts
}

export const updatePromptTestHelper = (prompts, promptId, testId, updates) => {
  return prompts.map((prompt) => {
    if (prompt.id !== promptId) {
      return prompt
    }
    return {
      ...prompt,
      prompt_tests: prompt.prompt_tests.map((test) => {
        if (test.id !== testId) {
          return test
        }
        return { ...test, ...updates }
      }),
    }
  })
}

// Legacy alias
export const updatePromptTestDescriptionHelper = (prompts, promptId, testId, newDescription) => {
  return updatePromptTestHelper(prompts, promptId, testId, { description: newDescription })
}
